import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import styles from "./EmailSendingLogsTab.module.css";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {useAppDispatch} from "../../../../../app/hooks";
import {showErrorToastNotification} from "../../../../elements/toastNotification/toastNotificationSlice";
import {TableLoader} from "../../../../elements/tableLoader/TableLoader";
import {TablePagination} from "../../../../elements/pagination/TablePagination";
import {EmailSendingLogUi, getEmailSendingLogs} from "./emailSendingLogsApi";
import {formatDateTime} from "../../../../../utils/dateTimeUtil";
import {getEmailSendingTypeTitle} from "../../../../../utils/enumValueTitleUtil";
import {ImageButton} from "../../../../elements/imageButton/ImageButton";
import {openPopupModal, PopupModalName} from "../../../../elements/popupModalProvider/popupModalSlice";

const ITEMS_PER_PAGE = 10;

interface EmailSendingLogsTabProps {
    botPublicId: string;
}

const formatCcRecipients = (ccRecipients: Record<string, string>): string => Object.entries(ccRecipients)
    .map(([title, email]) => title ? `${title}: ${email}` : email)
    .join(", ");

export const EmailSendingLogsTab: React.FC<EmailSendingLogsTabProps> = ({botPublicId}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [pageNumber, setPageNumber] = useState(0);
    const [logs, setLogs] = useState<EmailSendingLogUi[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [requestStatus, setRequestStatus] = useState(RequestStatus.NONE);
    const previousBotPublicId = useRef(botPublicId);

    useEffect(() => {
        if (previousBotPublicId.current !== botPublicId && pageNumber !== 0) {
            previousBotPublicId.current = botPublicId;
            setPageNumber(0);
            return;
        }
        previousBotPublicId.current = botPublicId;

        let isCurrentRequest = true;
        setRequestStatus(RequestStatus.PENDING);

        getEmailSendingLogs(botPublicId, pageNumber, ITEMS_PER_PAGE)
            .then(response => {
                if (!isCurrentRequest) return;
                setLogs(response.logs);
                setTotalCount(response.totalCount);
                setRequestStatus(RequestStatus.RESOLVED);
            })
            .catch(() => {
                if (!isCurrentRequest) return;
                setRequestStatus(RequestStatus.REJECTED);
                dispatch(showErrorToastNotification({message: "Failed to load email sending logs"}));
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [botPublicId, dispatch, pageNumber]);

    const isLoading = requestStatus === RequestStatus.PENDING;
    const isResolved = requestStatus === RequestStatus.RESOLVED;

    const handleViewRoutingDecision = (log: EmailSendingLogUi) => {
        if (log.routingDecision) {
            dispatch(openPopupModal({
                name: PopupModalName.VIEW_ROUTING_DECISION,
                popupModalProps: {routingDecision: log.routingDecision}
            }));
        }
    };

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
            <div className={styles.titleWrapper}>
                <h2>Email sending logs</h2>
                <p>Emails sent for this Assistant, including resolved recipients and transcript boundaries.</p>
            </div>

            <div className={styles.tableScrollWrapper}>
                <div className={styles.header}>
                    <span>Sent at</span>
                    <span>Type</span>
                    <span>Recipients</span>
                    <span>CC recipients</span>
                    <span className={styles.routingHeader}>Routing</span>
                </div>

                {isLoading && <TableLoader/>}

                {isResolved && logs.map(log => {
                    const {date, time, timezone} = formatDateTime(log.sentAt);
                    const transcriptLink = `/transcripts/${botPublicId}/${log.userId}?conversationChannel=${log.conversationChannel}`;

                    return <div className={styles.row} key={log.id} onClick={() => navigate(transcriptLink)}>
                        <div>{date}<br/>{time} {timezone}</div>
                        <span>{getEmailSendingTypeTitle(log.emailType)}</span>
                        <span>{log.recipients.join(", ")}</span>
                        <span>{formatCcRecipients(log.ccRecipients) || "—"}</span>
                        <div className={styles.routingCell} onClick={e => e.stopPropagation()}>
                            {log.routingDecision && (
                                <ImageButton onClick={() => handleViewRoutingDecision(log)}>
                                    <span className={styles.infoIcon}>i</span>
                                </ImageButton>
                            )}
                        </div>
                    </div>;
                })}

                {isResolved && logs.length === 0 && (
                    <div className={styles.emptyState}>No email sends have been logged for this Assistant yet.</div>
                )}
            </div>
        </div>

        {isResolved && <TablePagination
            pageNumber={pageNumber}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={totalCount}
            onPageChange={setPageNumber}
        />}
    </div>;
};
